import React, { useState, useRef, useEffect, useContext } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from "axios";
import { BASELINE } from "../../../util";
import { motion } from "framer-motion";
import useAIAudio from "../../aivoice/aivoice";
import PromotionModal from "../../../Components/popmodal";
import { UserContext } from "../../../context/UserContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const OverallAI = ({ type, analcomp, handleRowClick, report_eng, report_math }) => {
    const { user } = useContext(UserContext);

    // 음성 합성 옵션
    const voiceOptions = {
      gender: 'Female',
      voiceName: 'en-US-AriaNeural',
      rate: '15%',
      pitch: '9%',
      volume: '80%',
    };
  
    const [analai, setanalai] = useState("");
    const [aiplayed, setaiplayed] = useState(false);
  
    // (1) premium 여부에 따라, 실제 TTS로 넘길 텍스트 결정
    //     user.premium === 0 이면 "" 넘김
    const ttsText = user?.premium === 0 ? "" : analai;
  
    // (2) useAIAudio는 항상 호출 (조건부 X)
    const audioUrl = useAIAudio(ttsText, voiceOptions, 0);
  
    const audioRef = useRef(null);
    const [id, setid] = useState("");
    const [aitag, aisettag] = useState("");
    const [isPlaying, setIsPlaying] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
    }, Math.random() * 3000 + 2000);
    return () => clearInterval(interval);
  }, []);

  function analyzePerformance(combinedReport) {
    // Convert percentages to numeric values for comparison
    const parsedTopics = combinedReport.map((topic) => ({
      ...topic,
      correctPercentage: parseFloat(topic.correct.replace("%", "")), 
    }));

    // 빈 배열 방어 로직
    if (parsedTopics.length === 0) {
      return {
        greeting: "Hello! I’ve reviewed your Digital SAT 8 & 9 performance data.",
        performanceLevel: "No Data",
        topic: "No topic found",
        id: "",
        tag: "",
      };
    }

    // Find the topic with the lowest "correct" percentage
    const lowestTopic = parsedTopics.reduce((lowest, topic) => {
      return topic.correctPercentage < lowest.correctPercentage ? topic : lowest;
    });

    // Determine performance level
    const performanceLevel =
      lowestTopic.correctPercentage < 50 ? "Need to Study" : "Good Standing";

    return {
      greeting: "Hello! I’ve reviewed your Digital SAT 8 & 9 performance data.",
      performanceLevel,
      topic: lowestTopic.name,
      id: lowestTopic.id,
      tag: lowestTopic.tag,
    };
  }

  useEffect(() => {
    if (analcomp === 1) {
      const combinedReport = [
        ...report_eng.map((topic) => ({ ...topic, tag: "English" })),
        ...report_math.map((topic) => ({ ...topic, tag: "Math" })),
      ];
      const mularr = analyzePerformance(combinedReport);
      setid(mularr.topic);
      aisettag(mularr.tag);
      let prompt;
      if (type === 'SAT89') {
        prompt = `
          As the Digital SAT 8 & 9 Coordinator, your role is to recommend study topics in a friendly and encouraging way.
          Here is the user's study topic data:
          <Topic> ${mularr.topic} <Topic>
          Example output: "Hi, this is the AI assistance, I am recomending to study ${mularr.topic} to enhance the skill..."
          <<Please provide a short, friendly response that encourages the user to focus on the above topic.>>
        `;
      } else if (type === 'SAT10') {
        prompt = `
          As the Digital SAT 10 Coordinator, your role is to recommend study topics in a friendly and encouraging way.
          Here is the user's study topic data:
          <Topic> ${mularr.topic} <Topic>
          Example output: "Hi, this is the AI assistance, I am recomending to study ${mularr.topic} to enhance the skill..."
          <<Please provide a short, friendly response that encourages the user to focus on the above topic.>>
        `;
      } else if (type === 'SAT') {
        prompt = `
          As the Digital SAT Coordinator, your role is to recommend study topics in a friendly and encouraging way.
          Here is the user's study topic data:
          <Topic> ${mularr.topic} <Topic>
          Example output: "Hi, this is the AI assistance, I am recomending to study ${mularr.topic} to enhance the skill..."
          <<Please provide a short, friendly response that encourages the user to focus on the above topic.>>
        `;
      }

      if (analai === "") {
        askai(prompt);
      }
    }
  }, [analcomp]);

  const askai = async (prompt) => {
    try {
      const res = await axios.post(`${BASELINE}askai`, { prompt: prompt, type: type });
      if (res.data && res.data.message) {
        const answer = res.data.message.trim();
        setanalai(answer);
      } else {
        console.error('Unexpected response structure:', res.data);
      }
    } catch (error) {
      console.error('Error during API call:', error);
    }
  };

  const texttoaudio = async () => {
    // [추가] user.premium === 0 이면 모달 띄우고, return
    if (user && user.premium === 0) {
      setModalOpen(true); 
      return; 
    }

    // 이하 = 프리미엄 유저일 때만 오디오 처리
    if (audioUrl && !aiplayed) {
      setaiplayed(true);
      // Auto-play logic
      audioRef.current = new Audio(audioUrl);
      audioRef.current
        .play()
        .then(() => {
          audioRef.current.onended = () => {
            setIsPlaying(false);
          };
        })
        .catch((error) => {
          console.error('Error auto-playing audio:', error);
        });
    } else {
      setaiplayed(false);
      if (audioRef.current) {
        audioRef.current.pause(); 
        audioRef.current.currentTime = 0; 
      }
    }
  };

  return (
    <>
      <div className="relative">
        <PromotionModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

        {/* AI 버튼 */}
        <motion.div
          className="fixed bottom-[30px] right-[30px] flex items-center justify-center w-16 h-16 text-white font-bold text-lg rounded-full shadow-lg cursor-pointer"
          initial={{ scale: 1 }}
          animate={{
            scale: [1, 1.1, 1],
            background: [
              "linear-gradient(45deg, #4a90e2, #8e44ad)",
              "linear-gradient(45deg, #8e44ad, #4a90e2)",
              "linear-gradient(45deg, #4a90e2, #8e44ad)",
            ],
          }}
          transition={{
            scale: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            },
            background: {
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
          style={{
            boxShadow: "0 0 15px 5px rgba(138, 43, 226, 0.7)",
          }}
          onClick={texttoaudio}
        >
          AI
        </motion.div>

        {/* 말풍선 1 */}
        {showTooltip && aiplayed === false && (
          <motion.div
            className="fixed bottom-[100px] right-[25px] bg-gray-800 text-white text-sm w-[100px] p-2 rounded-md shadow-md"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.5 }}
          >
            <div className="ml-[9px]">Click me!</div>
          </motion.div>
        )}

        {aiplayed === true && (
          <>
            <motion.div
              className="fixed bottom-[200px] right-[25px] bg-gray-800 text-white text-sm w-[400px] p-4 rounded-md shadow-md"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5 }}
            >
              {analai}
            </motion.div>
            <motion.div
              className="fixed bottom-[145px] right-[25px] text-white text-sm w-[400px] p-4 rounded-md shadow-md cursor-pointer"
              onClick={() => handleRowClick(id, aitag)}
              initial={{ scale: 1 }}
              animate={{
                scale: [1, 1.1, 1],
                background: [
                  "linear-gradient(45deg, #4a90e2, #8e44ad)",
                  "linear-gradient(45deg, #8e44ad, #4a90e2)",
                  "linear-gradient(45deg, #4a90e2, #8e44ad)",
                ],
              }}
              whileHover={{
                scale: 1.1,
                transition: { duration: 0.2, ease: "easeInOut" },
              }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5 }}
              style={{
                cursor: 'url("path-to-your-cursor-image.png"), pointer',
              }}
            >
              ✨Generate the Practice test with AI!
            </motion.div>
          </>
        )}
      </div>
    </>
  );
};

export default OverallAI;
